import React, { useState } from 'react';
import {
  Calendar,
  Search,
  ChevronDown,
  ChevronUp,
  Plus,
  ArrowLeft,
  FileText,
  Download,
  Printer,
  X,
  Clock,
  UserCheck,
} from 'lucide-react';
import { ResidentSubMenuKey } from '../ResidentDirectory';

interface Props {
  onBack: () => void;
  onNavigate?: (tab: ResidentSubMenuKey) => void;
}

export interface OwnershipTransferRecord {
  id: string;
  transferDate: string; // e.g. "1994-03-08", "2021-10-25"
  roomNumber: string; // e.g. "1086/02", "1086/4448", "1086/01"
  transferorNames: string[]; // ผู้โอนกรรมสิทธิ์
  transfereeNames: string[]; // ผู้รับโอนกรรมสิทธิ์
  recordedBy: string; // บันทึกโดย (e.g. "silverman support")
  deedNumber?: string;
  notes?: string;
}

// Data exactly matching image 1 (image (7).png)
const INITIAL_TRANSFER_RECORDS: OwnershipTransferRecord[] = [
  {
    id: 'tr-1',
    transferDate: '1994-03-08',
    roomNumber: '1086/02',
    transferorNames: ['รสา ไชโย'],
    transfereeNames: ['สุชาย มีดี'],
    recordedBy: 'silverman support',
    deedNumber: '109823',
    notes: 'โอนกรรมสิทธิ์ตามสัญญาซื้อขายห้องชุด',
  },
  {
    id: 'tr-2',
    transferDate: '2021-10-25',
    roomNumber: '1086/4448',
    transferorNames: ['บริษัท เสนาดีเวลลอปเม้นท์ จำกัด (มหาชน)'],
    transfereeNames: ['เด็กดี มีสี'],
    recordedBy: 'silverman support',
    deedNumber: '109824',
    notes: 'โอนกรรมสิทธิ์จากโครงการเสนา',
  },
  {
    id: 'tr-3',
    transferDate: '2021-11-07',
    roomNumber: '1086/01',
    transferorNames: [
      'nikki rungchamras',
      'สวัสดีวันศุกร์',
      'tob tob',
      'อ่อย มะระ',
      'adisorn kaeophakdee',
    ],
    transfereeNames: ['test test'],
    recordedBy: 'silverman support',
    deedNumber: '109825',
    notes: 'โอนกรรมสิทธิ์ร่วมหลายบุคคล',
  },
  {
    id: 'tr-4',
    transferDate: '2021-11-08',
    roomNumber: '1086/05',
    transferorNames: ['เสนา'],
    transfereeNames: ['เทส กกกก'],
    recordedBy: 'silverman support',
    deedNumber: '109826',
  },
  {
    id: 'tr-5',
    transferDate: '2021-11-23',
    roomNumber: '1',
    transferorNames: ['桜春の', 'test123 test123'],
    transfereeNames: ['test123 test123', '桜春の'],
    recordedBy: 'silverman support',
    deedNumber: '109827',
  },
  {
    id: 'tr-6',
    transferDate: '2021-11-23',
    roomNumber: 'test',
    transferorNames: ['สมศักดิ์ เสมอภาค', 'สมหญิง ทานตะวัน'],
    transfereeNames: ['สมหญิง ทานตะวัน', 'สมศักดิ์ เสมอภาค'],
    recordedBy: 'silverman support',
    deedNumber: '109828',
  },
  {
    id: 'tr-7',
    transferDate: '2021-11-23',
    roomNumber: '1086/01',
    transferorNames: ['test test'],
    transfereeNames: ['เด็กดี มีสี'],
    recordedBy: 'silverman support',
    deedNumber: '109829',
  },
  {
    id: 'tr-8',
    transferDate: '2022-04-10',
    roomNumber: '1086/03',
    transferorNames: ['นายประเสริฐ ยั่งยืน'],
    transfereeNames: ['นางสาววิภาวรรณ สดใส'],
    recordedBy: 'silverman support',
    deedNumber: '109830',
  },
  {
    id: 'tr-9',
    transferDate: '2023-08-15',
    roomNumber: '1086/04',
    transferorNames: ['บริษัท มหาทรัพย์ พร็อพเพอร์ตี้ จำกัด'],
    transfereeNames: ['Mr. David Miller'],
    recordedBy: 'silverman support',
    deedNumber: '109831',
  },
  {
    id: 'tr-10',
    transferDate: '2024-01-20',
    roomNumber: '1086/06',
    transferorNames: ['นายอนุรักษ์ ร่มเย็น'],
    transfereeNames: ['คุณสมชาย ใจดี'],
    recordedBy: 'silverman support',
    deedNumber: '109832',
  },
];

export const ResidentTitleDeedsView: React.FC<Props> = ({ onBack, onNavigate }) => {
  const [records, setRecords] = useState<OwnershipTransferRecord[]>(INITIAL_TRANSFER_RECORDS);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<OwnershipTransferRecord | null>(null);

  // Add Form State
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formRoom, setFormRoom] = useState('1086/01');
  const [formTransferor, setFormTransferor] = useState('');
  const [formTransferee, setFormTransferee] = useState('');
  const [formRecorder, setFormRecorder] = useState('silverman support');
  const [formDeedNumber, setFormDeedNumber] = useState('');
  const [formNotes, setFormNotes] = useState('');

  const tabs: { key: ResidentSubMenuKey; label: string }[] = [
    { key: 'members', label: 'เจ้าของร่วม/สมาชิก' },
    { key: 'vehicles', label: 'ยานพาหนะ' },
    { key: 'keycards', label: 'คีย์การ์ด' },
    { key: 'foreign_ownership', label: 'กรรมสิทธิ์ชาวต่างชาติ' },
    { key: 'pets', label: 'สัตว์เลี้ยง' },
    { key: 'title_deeds', label: 'กรรมสิทธิ์' },
    { key: 'history_logs', label: 'ประวัติผู้อยู่อาศัย' },
  ];

  // Filtering
  const filteredRecords = records
    .filter((r) => {
      // Date range filter
      if (startDate && r.transferDate < startDate) return false;
      if (endDate && r.transferDate > endDate) return false;

      // Keyword search
      const q = searchQuery.toLowerCase().trim();
      if (!q) return true;

      const inRoom = r.roomNumber.toLowerCase().includes(q);
      const inTransferor = r.transferorNames.some((name) => name.toLowerCase().includes(q));
      const inTransferee = r.transfereeNames.some((name) => name.toLowerCase().includes(q));
      const inRecorder = r.recordedBy.toLowerCase().includes(q);
      const inDate = r.transferDate.includes(q);
      const inDeed = r.deedNumber?.toLowerCase().includes(q);

      return inRoom || inTransferor || inTransferee || inRecorder || inDate || inDeed;
    })
    .sort((a, b) => {
      if (sortAsc) {
        return a.transferDate.localeCompare(b.transferDate);
      }
      return b.transferDate.localeCompare(a.transferDate);
    });

  const handleAddTransfer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRoom || !formTransferor.trim() || !formTransferee.trim()) return;

    const transferors = formTransferor
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const transferees = formTransferee
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    const newRecord: OwnershipTransferRecord = {
      id: `tr-${Date.now()}`,
      transferDate: formDate,
      roomNumber: formRoom.trim(),
      transferorNames: transferors.length ? transferors : [formTransferor.trim()],
      transfereeNames: transferees.length ? transferees : [formTransferee.trim()],
      recordedBy: formRecorder.trim() || 'silverman support',
      deedNumber: formDeedNumber.trim() || undefined,
      notes: formNotes.trim() || undefined,
    };

    setRecords([newRecord, ...records]);
    setShowAddModal(false);
    setFormTransferor('');
    setFormTransferee('');
    setFormDeedNumber('');
    setFormNotes('');
  };

  const handleExportCSV = () => {
    let csv = 'วันที่โอนกรรมสิทธิ์,ห้อง,ผู้โอนกรรมสิทธิ์,ผู้รับโอนกรรมสิทธิ์,บันทึกโดย\n';
    records.forEach((r) => {
      const transferors = r.transferorNames.join(' / ');
      const transferees = r.transfereeNames.join(' / ');
      csv += `"${r.transferDate}","${r.roomNumber}","${transferors}","${transferees}","${r.recordedBy}"\n`;
    });

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ข้อมูลการเปลี่ยนกรรมสิทธิ์_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="font-prompt bg-white min-h-[85vh] rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
      {/* 1. Top Sub-Navigation Tabs matching uploaded image */}
      <div className="border-b border-slate-200 bg-slate-50/50 px-4 sm:px-6 pt-3 overflow-x-auto">
        <div className="flex items-center gap-6 sm:gap-8 min-w-max text-xs sm:text-sm">
          {tabs.map((tab) => {
            const isActive = tab.key === 'title_deeds';
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (tab.key === 'title_deeds') return;
                  if (onNavigate) {
                    onNavigate(tab.key);
                  } else {
                    onBack();
                  }
                }}
                className={`pb-3 font-medium transition-colors cursor-pointer relative ${
                  isActive
                    ? 'text-slate-900 font-bold border-b-2 border-teal-600'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* 2. Header & Action Row matching Image 1 */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Back Icon + Title */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors cursor-pointer shrink-0"
              title="ย้อนกลับ"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight">
              ข้อมูลการเปลี่ยนกรรมสิทธิ์
            </h1>
          </div>

          {/* Date Range Filter + Search Box matching image 1 */}
          <div className="flex flex-wrap items-center justify-end gap-3 sm:gap-4">
            {/* Date Range: วันที่ [ ] ถึง [ ] */}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
              <span className="font-medium shrink-0">วันที่</span>
              <div className="relative">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36 sm:w-40 px-2.5 py-1.5 bg-white border border-slate-300 rounded-md text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <span className="font-medium shrink-0">ถึง</span>
              <div className="relative">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36 sm:w-40 px-2.5 py-1.5 bg-white border border-slate-300 rounded-md text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            {/* Search Input Box */}
            <div className="relative w-48 sm:w-64">
              <input
                type="text"
                placeholder="ค้นหา"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-3 pr-8 py-1.5 bg-white border border-slate-300 rounded-md text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              />
              <Search className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* Add & Export Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="px-3 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-md text-xs font-medium flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" /> บันทึกการเปลี่ยนกรรมสิทธิ์
              </button>
              <button
                type="button"
                onClick={handleExportCSV}
                title="ส่งออก CSV / Excel"
                className="p-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-600 rounded-md text-xs transition-colors cursor-pointer"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* 3. Table matching Image 1 layout */}
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-2xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm text-slate-700">
              <thead className="bg-[#f1f3f5] border-b border-slate-200 text-xs font-semibold text-slate-800">
                <tr>
                  {/* วันที่โอนกรรมสิทธิ์ with downward arrow */}
                  <th
                    className="py-3 px-4 w-44 font-semibold text-slate-800 cursor-pointer select-none hover:bg-slate-200/70 transition-colors"
                    onClick={() => setSortAsc(!sortAsc)}
                  >
                    <div className="flex items-center gap-1">
                      <span>วันที่โอนกรรมสิทธิ์</span>
                      {sortAsc ? (
                        <ChevronUp className="w-3.5 h-3.5 text-slate-600" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5 text-slate-600" />
                      )}
                    </div>
                  </th>
                  <th className="py-3 px-4 w-32 font-semibold text-slate-800">ห้อง</th>
                  <th className="py-3 px-4 font-semibold text-slate-800">ผู้โอนกรรมสิทธิ์</th>
                  <th className="py-3 px-4 font-semibold text-slate-800">ผู้รับโอนกรรมสิทธิ์</th>
                  <th className="py-3 px-4 w-44 font-semibold text-slate-800">บันทึกโดย</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400 text-xs">
                      ไม่พบข้อมูลการเปลี่ยนกรรมสิทธิ์ตามเงื่อนไขที่ค้นหา
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedRecord(r)}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer"
                    >
                      {/* วันที่โอนกรรมสิทธิ์ */}
                      <td className="py-3.5 px-4 font-mono text-slate-800 align-top whitespace-nowrap">
                        {r.transferDate}
                      </td>

                      {/* ห้อง */}
                      <td className="py-3.5 px-4 font-medium text-slate-800 align-top whitespace-nowrap">
                        {r.roomNumber}
                      </td>

                      {/* ผู้โอนกรรมสิทธิ์ (รองรับหลายบรรทัด) */}
                      <td className="py-3.5 px-4 text-slate-800 align-top">
                        <div className="space-y-0.5">
                          {r.transferorNames.map((name, idx) => (
                            <div key={idx} className="leading-snug">
                              {name}
                            </div>
                          ))}
                        </div>
                      </td>

                      {/* ผู้รับโอนกรรมสิทธิ์ (รองรับหลายบรรทัด) */}
                      <td className="py-3.5 px-4 text-slate-800 align-top">
                        <div className="space-y-0.5">
                          {r.transfereeNames.map((name, idx) => (
                            <div key={idx} className="leading-snug">
                              {name}
                            </div>
                          ))}
                        </div>
                      </td>

                      {/* บันทึกโดย */}
                      <td className="py-3.5 px-4 text-slate-600 align-top whitespace-nowrap">
                        {r.recordedBy}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 4. Table Summary Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 pt-2 px-1">
          <div>
            แสดง {filteredRecords.length} จากทั้งหมด {records.length} รายการ
          </div>
          {(startDate || endDate || searchQuery) && (
            <button
              type="button"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setSearchQuery('');
              }}
              className="text-teal-700 hover:underline cursor-pointer font-medium"
            >
              ล้างตัวกรองทั้งหมด
            </button>
          )}
        </div>
      </div>

      {/* Modal: View Record Detail */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95">
            <div className="bg-teal-800 p-4 sm:p-5 text-white flex items-center justify-between">
              <h3 className="font-bold text-base flex items-center gap-2">
                <FileText className="w-5 h-5" /> รายละเอียดการเปลี่ยนกรรมสิทธิ์
              </h3>
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="text-white/80 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs sm:text-sm">
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div>
                  <span className="text-slate-400 block text-[11px]">ห้อง / บ้านเลขที่</span>
                  <span className="font-bold text-slate-800 text-sm">{selectedRecord.roomNumber}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px]">วันที่โอนกรรมสิทธิ์</span>
                  <span className="font-mono font-semibold text-slate-800">{selectedRecord.transferDate}</span>
                </div>
                {selectedRecord.deedNumber && (
                  <div>
                    <span className="text-slate-400 block text-[11px]">เลขที่โฉนดที่ดิน</span>
                    <span className="font-mono font-semibold text-slate-800">{selectedRecord.deedNumber}</span>
                  </div>
                )}
                <div>
                  <span className="text-slate-400 block text-[11px]">บันทึกข้อมูลโดย</span>
                  <span className="text-slate-800">{selectedRecord.recordedBy}</span>
                </div>
              </div>

              <div>
                <span className="font-semibold text-slate-700 block mb-1">ผู้โอนกรรมสิทธิ์:</span>
                <div className="bg-rose-50/60 border border-rose-100 rounded-lg p-2.5 space-y-1">
                  {selectedRecord.transferorNames.map((name, i) => (
                    <div key={i} className="text-slate-800 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                      {name}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <span className="font-semibold text-slate-700 block mb-1">ผู้รับโอนกรรมสิทธิ์ (เจ้าของใหม่):</span>
                <div className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-2.5 space-y-1">
                  {selectedRecord.transfereeNames.map((name, i) => (
                    <div key={i} className="text-slate-800 flex items-center gap-2 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      {name}
                    </div>
                  ))}
                </div>
              </div>

              {selectedRecord.notes && (
                <div>
                  <span className="font-semibold text-slate-700 block mb-1">หมายเหตุ:</span>
                  <p className="text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                    {selectedRecord.notes}
                  </p>
                </div>
              )}

              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold cursor-pointer"
                >
                  ปิดหน้าต่าง
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add Transfer Record */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 my-8">
            <div className="bg-teal-800 p-4 sm:p-5 text-white flex items-center justify-between">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Plus className="w-5 h-5" /> บันทึกการเปลี่ยนกรรมสิทธิ์ใหม่
              </h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-white/80 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddTransfer} className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    วันที่โอนกรรมสิทธิ์ *
                  </label>
                  <input
                    type="date"
                    required
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    เลขที่ห้อง / บ้านเลขที่ *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น 1086/01"
                    value={formRoom}
                    onChange={(e) => setFormRoom(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  ผู้โอนกรรมสิทธิ์ (เดิม) * <span className="text-slate-400 font-normal">(ขึ้นบรรทัดใหม่ได้หากมีหลายท่าน)</span>
                </label>
                <textarea
                  required
                  rows={2}
                  placeholder="กรอกชื่อ-นามสกุล ผู้โอนกรรมสิทธิ์"
                  value={formTransferor}
                  onChange={(e) => setFormTransferor(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  ผู้รับโอนกรรมสิทธิ์ (ใหม่) * <span className="text-slate-400 font-normal">(ขึ้นบรรทัดใหม่ได้หากมีหลายท่าน)</span>
                </label>
                <textarea
                  required
                  rows={2}
                  placeholder="กรอกชื่อ-นามสกุล ผู้รับโอนกรรมสิทธิ์"
                  value={formTransferee}
                  onChange={(e) => setFormTransferee(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    เลขที่โฉนดที่ดิน (ถ้ามี)
                  </label>
                  <input
                    type="text"
                    placeholder="เช่น 109833"
                    value={formDeedNumber}
                    onChange={(e) => setFormDeedNumber(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    บันทึกข้อมูลโดย
                  </label>
                  <input
                    type="text"
                    value={formRecorder}
                    onChange={(e) => setFormRecorder(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  หมายเหตุเพิ่มเติม
                </label>
                <input
                  type="text"
                  placeholder="เช่น โอนกรรมสิทธิ์ตามพินัยกรรม / นิติกรรมซื้อขาย"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold cursor-pointer"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-xs font-semibold shadow-xs cursor-pointer"
                >
                  บันทึกข้อมูล
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
